import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import SwiftAlert from "../../components/SwiftAlert";
import { io } from "socket.io-client";
import { API_URL } from "../../services/api/config";
import Swal from "sweetalert2";
import {
  getStudyGroupMessages,
  getStudyGroups,
  sendStudyGroupMessage,
  getStudyGroupMembers,
  deleteStudyGroupMessage,
  markStudyGroupChatAsRead,
  getGroupReviewerContent,
  getActiveChallenge,
  createChallenge,
  cancelChallenge,
} from "../../services/api";
import apiClient from "../../services/api/config";
import { useAuth } from "../../contexts/AuthContext";
import { checkEnabled } from "../../utils/libraryUtils";
import HubBanner from "../../components/StudyGroupHub/HubBanner";
import HubFilters from "../../components/StudyGroupHub/HubFilters";
import SharedReviewerCard from "../../components/StudyGroupHub/SharedReviewerCard";
import ActiveChallengeBanner from "../../components/StudyGroupHub/ActiveChallengeBanner";
import HistoryLeaderboardModal from "../../components/StudyGroupHub/modals/HistoryLeaderboardModal";
import MessageDetailsModal from "../../components/StudyGroupHub/modals/MessageDetailsModal";
import HubChatDrawer from "../../components/StudyGroupHub/modals/HubChatDrawer";
import ShareReviewerModal from "../../components/StudyGroupHub/modals/ShareReviewerModal";
import HubMembersModal from "../../components/StudyGroupHub/modals/HubMembersModal";
import CreateChallengeModal from "../../components/StudyGroupHub/modals/CreateChallengeModal";
import ChallengeLeaderboardModal from "../../components/StudyGroupHub/modals/ChallengeLeaderboardModal";
import ChallengeQuizModal from "../../components/StudyGroupHub/modals/ChallengeQuizModal";
import ChallengeHistoryModal from "../../components/StudyGroupHub/modals/ChallengeHistoryModal";

const StudyGroupHub = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [reviewerCode, setReviewerCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sharingCode, setSharingCode] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [members, setMembers] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [hasUnreadMessage, setHasUnreadMessage] = useState(false);
  const [lastReadChat, setLastReadChat] = useState(null);
  const [openCardMenuId, setOpenCardMenuId] = useState(null);
  const [historyModal, setHistoryModal] = useState({ open: false, title: "", history: [], loading: false });
  const [replyingTo, setReplyingTo] = useState(null);
  const [liveAvatars, setLiveAvatars] = useState({});
  const [alert, setAlert] = useState(null);

  // Challenge Mode State
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [viewingChallenge, setViewingChallenge] = useState(null);
  const [isCreateChallengeOpen, setIsCreateChallengeOpen] = useState(false);
  const [challengeTargetReviewerId, setChallengeTargetReviewerId] = useState(null);
  const [isChallengeLeaderboardOpen, setIsChallengeLeaderboardOpen] = useState(false);
  const [isChallengeQuizOpen, setIsChallengeQuizOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  // Search & Filter state for shared reviewers
  const [searchQuery, setSearchQuery] = useState("");
  const [modeFilter, setModeFilter] = useState("all"); // 'all' | 'quiz' | 'cards' | 'summary'

  const chatInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isChatOpenRef = useRef(isChatOpen);
  const userRef = useRef(user);
  const cardMenuRef = useRef(null);

  const scrollToMessage = (msgId) => {
    if (!msgId) return;
    const el = document.getElementById(`msg-${msgId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#2563EB]", "rounded-2xl");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-[#2563EB]", "rounded-2xl");
      }, 1500);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
    if (isChatOpen) {
      setHasUnreadMessage(false);
      setLastReadChat(new Date());
      markStudyGroupChatAsRead(id).catch(() => { });
      window.dispatchEvent(
        new CustomEvent("study_group_chat_read", { detail: { groupId: String(id) } })
      );
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isChatOpen, id]);

  // Click outside to dismiss card menu
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (cardMenuRef.current && !cardMenuRef.current.contains(e.target)) {
        setOpenCardMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Compute unread badge
  useEffect(() => {
    if (!user || isChatOpenRef.current) return;
    const lastRead = lastReadChat ? new Date(lastReadChat).getTime() : 0;
    const currentUserId = String(user._id || user.id || "");
    const hasUnread = messages.some((m) => {
      const msgTime = new Date(m.createdAt).getTime();
      const senderId = String(m?.sender?._id || m?.sender?.id || "");
      return msgTime > lastRead && senderId !== currentUserId;
    });
    if (hasUnread) {
      setHasUnreadMessage(true);
    }
  }, [messages, user, lastReadChat]);

  useEffect(() => {
    const handleGlobalMsg = (e) => {
      if (e?.detail?.groupId && String(e.detail.groupId) === String(id) && !isChatOpenRef.current) {
        setHasUnreadMessage(true);
      }
    };
    window.addEventListener("study_group_message_received", handleGlobalMsg);
    return () => window.removeEventListener("study_group_message_received", handleGlobalMsg);
  }, [id]);


  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const sharedReviewers = useMemo(
    () => messages.filter((m) => m.type === "reviewer_code"),
    [messages]
  );

  // Filtered reviewers
  const filteredReviewers = useMemo(() => {
    let list = [...sharedReviewers];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((msg) => {
        const title = msg?.reviewer?.topic || msg?.reviewer?.title || msg?.text || "";
        const sender = `${msg?.sender?.firstName || ""} ${msg?.sender?.lastName || ""}`;
        return title.toLowerCase().includes(q) || sender.toLowerCase().includes(q);
      });
    }

    if (modeFilter === "quiz") {
      list = list.filter((msg) => checkEnabled(msg?.reviewer?.config?.quizEnabled));
    } else if (modeFilter === "cards") {
      list = list.filter((msg) => checkEnabled(msg?.reviewer?.config?.flashcardsEnabled));
    } else if (modeFilter === "summary") {
      list = list.filter((msg) => checkEnabled(msg?.reviewer?.config?.summaryEnabled));
    }

    return list;
  }, [sharedReviewers, searchQuery, modeFilter]);

  // Avatar lookup map
  const memberAvatarMap = useMemo(() => {
    const map = {};
    (members || []).forEach((m) => {
      if (m.userId) map[String(m.userId)] = m.avatar || "";
    });
    Object.keys(liveAvatars).forEach((uid) => {
      map[uid] = liveAvatars[uid];
    });
    if (user?._id && user?.avatar) {
      map[String(user._id)] = liveAvatars[String(user._id)] || user.avatar;
    }
    return map;
  }, [members, user, liveAvatars]);

  const loadHub = async () => {
    try {
      setLoading(true);
      const groups = await getStudyGroups();
      const current = (groups || []).find((g) => String(g._id) === String(id));
      if (!current) {
        navigate("/study-group");
        return;
      }
      setGroup(current);
      setLastReadChat(current.lastReadChat || null);
      const msgs = await getStudyGroupMessages(id);
      const msgList = Array.isArray(msgs) ? msgs : [];
      setMessages(msgList);

      const membersData = await getStudyGroupMembers(id);
      setMembers(membersData?.members || []);

      const challengeData = await getActiveChallenge(id).catch(() => null);
      setActiveChallenge(challengeData || null);
    } catch {
      navigate("/study-group");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;

    const socketUrl = API_URL.replace(/\/api$/, "");
    const token = localStorage.getItem("jwt_token");
    const newSocket = io(socketUrl, {
      withCredentials: true,
      path: "/api/socket.io",
      auth: token ? { token } : undefined,
    });

    newSocket.emit("join_group", id);

    newSocket.on("new_message", (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => String(m._id) === String(msg._id))) return prev;

        const senderId = String(msg.sender?._id || msg.sender?.id || "");
        const myId = String(userRef.current?._id || userRef.current?.id || "");
        if (
          !isChatOpenRef.current &&
          (!myId || senderId !== myId)
        ) {
          setHasUnreadMessage(true);
        }

        return [...prev, msg].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });
    });

    newSocket.on("message_deleted", (deleted) => {
      const deletedId = typeof deleted === "object" ? deleted?.messageId || deleted?._id : deleted;
      if (!deletedId) return;

      setMessages((prev) => prev.filter((m) => String(m._id) !== String(deletedId)));
      setReplyingTo((current) =>
        current && String(current._id) === String(deletedId) ? null : current
      );
    });

    newSocket.on("user_avatar_updated", (data) => {
      if (!data?.userId || !data?.avatar) return;
      setLiveAvatars((prev) => ({
        ...prev,
        [String(data.userId)]: data.avatar,
      }));
    });

    newSocket.on("challenge_created", (data) => {
      if (data?.challenge) {
        setActiveChallenge(data.challenge);
        setAlert({
          type: "info",
          title: "New Challenge!",
          message: `${data.challenge.title} has started!`,
        });
      }
    });

    newSocket.on("challenge_score_submitted", (data) => {
      setActiveChallenge((prev) => {
        if (!prev || String(prev._id) !== String(data.challengeId)) return prev;
        const myId = String(userRef.current?._id || userRef.current?.id || "");
        const myAttempt = data.leaderboard?.find((p) => String(p.userId) === myId);
        return {
          ...prev,
          leaderboard: data.leaderboard,
          participantsCount: data.participantsCount,
          hasUserAttempted: prev.hasUserAttempted || Boolean(myAttempt),
          userAttempt: myAttempt || prev.userAttempt,
          userRank: myAttempt?.rank || prev.userRank,
        };
      });
    });

    newSocket.on("challenge_new_leader", (data) => {
      if (data?.message) {
        setAlert({
          type: "warning",
          title: "👑 New Leader!",
          message: data.message,
        });
      }
    });

    newSocket.on("challenge_cancelled", (data) => {
      setActiveChallenge((prev) =>
        prev && String(prev._id) === String(data.challengeId) ? null : prev
      );
      setAlert({
        type: "info",
        title: "Challenge Cancelled",
        message: "The active challenge has been cancelled.",
      });
    });

    return () => {
      newSocket.disconnect();
    };
  }, [id]);

  const title = useMemo(() => group?.name || "Study Group Hub", [group]);

  const sendMessage = async () => {
    const text = messageInput.trim();
    if (!text) return;
    try {
      setSending(true);
      const created = await sendStudyGroupMessage(id, {
        type: "text",
        text,
        replyTo: replyingTo?._id || null,
      });
      if (created) {
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(created._id))) return prev;
          return [...prev, created];
        });
      }
      setMessageInput("");
      setReplyingTo(null);
    } finally {
      setSending(false);
    }
  };

  const shareCode = async () => {
    const code = reviewerCode.trim().toUpperCase();
    if (!code) return;
    try {
      setSharingCode(true);
      const created = await sendStudyGroupMessage(id, {
        type: "reviewer_code",
        reviewerCode: code,
      });
      if (created) {
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(created._id))) return prev;
          return [...prev, created];
        });
        setReviewerCode("");
        setIsShareModalOpen(false);
        setAlert({
          type: "success",
          title: "Reviewer Shared!",
          message: "The reviewer has been shared with your group.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Share Failed",
        text: err.response?.data?.message || err.message || "Shared code not found.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setSharingCode(false);
    }
  };

  const handleStudyAction = async (code, reviewerId, mode) => {
    if (!reviewerId) return;
    try {
      setRedeeming(true);
      const res = await getGroupReviewerContent(id, reviewerId);
      if (res?.meta && res?.data) {
        navigate(`/study-mode/${reviewerId}`, {
          state: {
            mode,
            noteData: {
              id: reviewerId,
              title: res.meta.topic || "Reviewer",
              subject: "—",
              verified: res.meta.verified || false,
              txHash: res.meta.txHash || null,
              config: res.meta.config || {},
            },
            preloadedContent: res.data,
            isGroupShared: true,
          },
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "Failed to access reviewer.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setRedeeming(false);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    try {
      const result = await Swal.fire({
        title: "Remove Reviewer?",
        text: "This will remove the shared reviewer from the hub for everyone in this group.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EF4444",
        cancelButtonColor: "#6B7280",
        confirmButtonText: "Remove Reviewer",
      });

      if (result.isConfirmed) {
        await deleteStudyGroupMessage(id, msgId);
        setMessages((prev) => prev.filter((m) => m._id !== msgId));
        setAlert({
          type: "success",
          title: "Reviewer Removed",
          message: "Reviewer was removed from the group hub.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "Failed to remove reviewer.",
        confirmButtonColor: "#2563EB",
      });
    }
  };

  const handleViewHistory = async (reviewerId, reviewerTitle) => {
    setOpenCardMenuId(null);
    setHistoryModal({ open: true, title: reviewerTitle, history: [], loading: true });
    try {
      const res = await apiClient.get(`/study-groups/${id}/hub/history/${reviewerId}`);
      setHistoryModal((prev) => ({ ...prev, loading: false, history: res.data?.data || [] }));
    } catch {
      setHistoryModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCreateChallenge = async (payload) => {
    const created = await createChallenge(id, payload);
    if (created) {
      const freshActive = await getActiveChallenge(id).catch(() => created);
      setActiveChallenge(freshActive || created);
      setAlert({
        type: "success",
        title: "Challenge Launched!",
        message: `"${payload.title}" is now active in your study group.`,
      });
    }
  };

  const handleCancelChallenge = async () => {
    if (!activeChallenge?._id) return;
    try {
      const res = await Swal.fire({
        title: "Cancel Challenge?",
        text: "Are you sure you want to cancel this challenge for the entire study group?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EF4444",
        cancelButtonColor: "#6B7280",
        confirmButtonText: "Yes, cancel it",
      });

      if (res.isConfirmed) {
        await cancelChallenge(id, activeChallenge._id);
        setActiveChallenge(null);
        setAlert({
          type: "success",
          title: "Challenge Cancelled",
          message: "The challenge has been cancelled.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || err.message || "Failed to cancel challenge.",
      });
    }
  };

  const handleOpenCreateChallenge = (reviewerId) => {
    if (activeChallenge) {
      setIsChallengeLeaderboardOpen(true);
      return;
    }
    setChallengeTargetReviewerId(reviewerId || null);
    setIsCreateChallengeOpen(true);
  };

  const handleAcceptChallenge = () => {
    if (!activeChallenge) return;
    setIsChallengeQuizOpen(true);
  };

  const handleAttemptSubmitted = async (result) => {
    const payload = result?.data || result || {};
    const updatedLeaderboard = Array.isArray(payload.leaderboard) ? payload.leaderboard : [];
    const userStanding = payload.userAttempt || null;
    const userRank = payload.rank || userStanding?.rank || null;

    setActiveChallenge((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        hasUserAttempted: true,
        userAttempt: userStanding || prev.userAttempt,
        userRank: userRank || prev.userRank,
        leaderboard: updatedLeaderboard.length > 0 ? updatedLeaderboard : prev.leaderboard,
        participantsCount:
          updatedLeaderboard.length > 0 ? updatedLeaderboard.length : prev.participantsCount,
      };
    });

    // Re-sync with backend to guarantee consistent server state
    try {
      const refreshed = await getActiveChallenge(id);
      if (refreshed) {
        setActiveChallenge(refreshed);
      }
    } catch {
      // ignore
    }

    setIsChallengeLeaderboardOpen(true);
    setAlert({
      type: "success",
      title: "Challenge Completed!",
      message: `You scored ${userStanding?.totalPoints || 0} points!`,
    });
  };

  const handleCopyGroupInviteCode = async () => {
    if (!group?.inviteCode) {
      setAlert({
        type: "info",
        title: "No Invite Code",
        message: "No active invite code found for this group.",
      });
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(group.inviteCode);
      }
      setAlert({
        type: "success",
        title: "Invite Code Copied!",
        message: `Group code ${group.inviteCode} copied to clipboard.`,
      });
    } catch {
      setAlert({
        type: "info",
        title: "Group Code",
        message: group.inviteCode,
      });
    }
  };

  return (
    <div className="bg-[#F8F9FA] text-slate-800 flex h-screen overflow-hidden font-sans antialiased">
      {alert && <SwiftAlert {...alert} onClose={() => setAlert(null)} />}

      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} title={title} />

        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50/60">
          <div className="max-w-[1400px] mx-auto w-full space-y-6 flex flex-col min-h-full">

            <HubBanner
              group={group}
              user={user}
              sharedReviewersCount={sharedReviewers.length}
              membersCount={members.length}
              messagesCount={messages.length}
              hasUnreadMessage={hasUnreadMessage}
              hasActiveChallenge={Boolean(activeChallenge)}
              onCopyInviteCode={handleCopyGroupInviteCode}
              onOpenMembers={() => setIsMembersModalOpen(true)}
              onOpenShareModal={() => setIsShareModalOpen(true)}
              onOpenChat={() => setIsChatOpen(true)}
              onOpenChallenge={() => {
                setViewingChallenge(null);
                activeChallenge ? setIsChallengeLeaderboardOpen(true) : setIsCreateChallengeOpen(true);
              }}
            />

            {/* ACTIVE CHALLENGE BANNER */}
            {activeChallenge && (
              <ActiveChallengeBanner
                challenge={activeChallenge}
                user={user}
                group={group}
                onAcceptChallenge={handleAcceptChallenge}
                onViewLeaderboard={() => {
                  setViewingChallenge(null);
                  setIsChallengeLeaderboardOpen(true);
                }}
                onCancelChallenge={handleCancelChallenge}
                onOpenHistory={() => setIsHistoryModalOpen(true)}
              />
            )}

            <HubFilters
              modeFilter={modeFilter}
              setModeFilter={setModeFilter}
              sharedReviewersCount={sharedReviewers.length}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onOpenHistory={() => setIsHistoryModalOpen(true)}
            />

            {/* SHARED REVIEWERS GRID */}
            <div className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {[1, 2, 3].map((n) => (
                    <div
                      key={n}
                      className="bg-white rounded-3xl p-5 border border-gray-100 shadow-sm animate-pulse h-60 flex flex-col justify-between"
                    >
                      <div className="flex items-center justify-between">
                        <div className="w-10 h-10 rounded-xl bg-gray-200"></div>
                        <div className="w-20 h-5 rounded-full bg-gray-200"></div>
                      </div>
                      <div className="w-3/4 h-5 bg-gray-200 rounded"></div>
                      <div className="w-full h-10 bg-gray-200 rounded-xl"></div>
                    </div>
                  ))}
                </div>
              ) : filteredReviewers.length === 0 ? (
                <div className="bg-white rounded-3xl border border-gray-100 p-10 md:p-14 text-center max-w-lg mx-auto shadow-sm">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl mx-auto mb-4">
                    <i className="fa-solid fa-book-open"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {searchQuery || modeFilter !== "all"
                      ? "No reviewers match your search"
                      : "No reviewers shared yet"}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    {searchQuery || modeFilter !== "all"
                      ? "Try resetting your filter or searching for another term."
                      : "Share your first reviewer code into this group so members can study together."}
                  </p>
                  {searchQuery || modeFilter !== "all" ? (
                    <button
                      onClick={() => {
                        setSearchQuery("");
                        setModeFilter("all");
                      }}
                      className="px-5 py-2.5 rounded-xl font-bold text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                    >
                      Clear Filters
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsShareModalOpen(true)}
                      className="px-5 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-700 text-white transition shadow-sm"
                    >
                      <i className="fa-solid fa-plus mr-2 text-xs"></i> Share Reviewer Code
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredReviewers.map((msg) => (
                    <SharedReviewerCard
                      key={msg._id}
                      msg={msg}
                      group={group}
                      user={user}
                      openCardMenuId={openCardMenuId}
                      setOpenCardMenuId={setOpenCardMenuId}
                      cardMenuRef={cardMenuRef}
                      liveAvatars={liveAvatars}
                      memberAvatarMap={memberAvatarMap}
                      redeeming={redeeming}
                      onStudyAction={handleStudyAction}
                      onViewHistory={handleViewHistory}
                      onDeleteMessage={handleDeleteMessage}
                      onLaunchChallenge={handleOpenCreateChallenge}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* MODALS & DRAWERS */}
      <HistoryLeaderboardModal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal((h) => ({ ...h, open: false }))}
        title={historyModal.title}
        loading={historyModal.loading}
        history={historyModal.history}
        liveAvatars={liveAvatars}
      />

      <MessageDetailsModal
        selectedMessage={selectedMessage}
        onClose={() => setSelectedMessage(null)}
        liveAvatars={liveAvatars}
        memberAvatarMap={memberAvatarMap}
        onStudyAction={handleStudyAction}
        redeeming={redeeming}
      />

      <HubChatDrawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        group={group}
        user={user}
        messages={messages}
        setMessages={setMessages}
        memberAvatarMap={memberAvatarMap}
        replyingTo={replyingTo}
        setReplyingTo={setReplyingTo}
        messageInput={messageInput}
        setMessageInput={setMessageInput}
        sending={sending}
        sendMessage={sendMessage}
        scrollToMessage={scrollToMessage}
        onSelectMessage={(msg) => setSelectedMessage(msg)}
        onSelectChallenge={() => {
          setIsChatOpen(false);
          setIsChallengeLeaderboardOpen(true);
        }}
        messagesEndRef={messagesEndRef}
        chatInputRef={chatInputRef}
        groupId={id}
        deleteStudyGroupMessage={deleteStudyGroupMessage}
        Swal={Swal}
      />

      <ShareReviewerModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        reviewerCode={reviewerCode}
        setReviewerCode={setReviewerCode}
        sharingCode={sharingCode}
        onShare={shareCode}
      />

      <HubMembersModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        members={members}
        group={group}
        liveAvatars={liveAvatars}
        memberAvatarMap={memberAvatarMap}
      />

      <CreateChallengeModal
        isOpen={isCreateChallengeOpen}
        onClose={() => setIsCreateChallengeOpen(false)}
        groupId={id}
        sharedReviewers={sharedReviewers}
        initialReviewerId={challengeTargetReviewerId}
        onCreateChallenge={handleCreateChallenge}
      />

      <ChallengeLeaderboardModal
        isOpen={isChallengeLeaderboardOpen}
        onClose={() => {
          setIsChallengeLeaderboardOpen(false);
          setViewingChallenge(null);
        }}
        challenge={viewingChallenge || activeChallenge}
        user={user}
        liveAvatars={liveAvatars}
        onAcceptChallenge={handleAcceptChallenge}
      />

      <ChallengeQuizModal
        isOpen={isChallengeQuizOpen}
        onClose={() => setIsChallengeQuizOpen(false)}
        challenge={activeChallenge}
        groupId={id}
        onAttemptSubmitted={handleAttemptSubmitted}
      />

      <ChallengeHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        groupId={id}
        liveAvatars={liveAvatars}
        onSelectPastChallenge={(pastChallenge) => {
          setViewingChallenge(pastChallenge);
          setIsHistoryModalOpen(false);
          setIsChallengeLeaderboardOpen(true);
        }}
      />

    </div>
  );
};

export default StudyGroupHub;
